import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import sortBy from 'lodash.sortby';
import { LeaderImageCard } from '../../LeaderImageCard/LeaderImageCard';
import {
    createGridMatrix,
    adjustGridRows,
    getCardGap,
    getLeaderInputs,
} from './LeaderGridUtils';
import { useScreenSize } from '../../../shared/useScreenSize';
import { useLeaderCardQuery } from './useLeaderCardQuery';
import { ILeaderData } from '../LeaderPageTypes';

interface ILeaderGrid {
    edic: boolean;
    /** The max width of the grid */
    maxGridWidth: number;
    /** Used to set the max width of the grid so other components on the page can be constrained to this size */
    setMaxGridWidth: React.Dispatch<React.SetStateAction<number>>;
}

const LeaderGrid: FC<ILeaderGrid> = ({
    edic,
    maxGridWidth,
    setMaxGridWidth,
}) => {
    const [maxRowCount, setMaxRowCount] = useState<number>();
    const [grid, setGrid] = useState([]);
    const gridRef = useRef(null);
    const screenSize = useScreenSize();
    const gridEl = gridRef.current;
    const cardWidth = 252;
    const gapWidth = 16;
    const maxGridWidths = {
        4: 1140,
        3: 820,
        2: 528,
        1: 330,
    };

    const { leaders, page } = useLeaderCardQuery(edic);

    const gridWidthRef = useRef(null);

    const leadersSorted = sortBy(leaders, (leader: ILeaderData) =>
        leader.field_last_name.toLowerCase()
    );

    const textBlocks = page?.relationships?.field_components?.flatMap(
        (data) => {
            if (data.type === `paragraph__leadership_text_row`) {
                return [
                    {
                        text: data.field_text,
                        row: data.field_row_number,
                    },
                ];
            }
            return [];
        }
    );

    useEffect(() => {
        let maxRow;
        // Determines the max number of cards that can be in a row based on the size of the cards and the minimum gap between them.
        for (let count = 4; count > 0; count--) {
            const rowWidth = cardWidth * count + gapWidth * (count - 1);
            if (rowWidth > gridEl?.clientWidth) {
                continue;
            } else {
                setMaxRowCount(count);
                maxRow = count;
                break;
            }
        }

        const leaderGrid = createGridMatrix(leadersSorted, maxRow);
        const renderGrid = adjustGridRows(leaderGrid, maxRow);

        // Inserts the text blocks into their specified grid rows
        textBlocks?.forEach((tb) => {
            if (!tb.row) return;
            // Protects against negative insert indexes
            const insertIdx = Number(tb.row) - 1 <= 0 ? 0 : Number(tb.row) - 1;
            // Does not insert if the insert row is greater than the highest row count
            if (insertIdx > renderGrid?.length) return;
            renderGrid.splice(insertIdx, 0, tb);
        });

        setGrid(renderGrid);
    }, [screenSize, gridEl]);

    useLayoutEffect(() => {
        setMaxGridWidth(gridWidthRef.current?.clientWidth);
    });

    if (!grid?.length) return;
    return (
        <div className="leaders" ref={gridRef}>
            {grid?.map((gItem, idx) => {
                // The item is a text block
                if (gItem?.text) {
                    return (
                        <div
                            key={`${gItem.text}${gItem.row}`}
                            className="grid-row grid-row--text"
                            style={{ maxWidth: `${maxGridWidth}px` }}
                        >
                            <p>{gItem.text}</p>
                        </div>
                    );
                }
                // The item is an array of cards
                else {
                    const isEvenRow = gItem?.length < maxRowCount;
                    const rowClass = isEvenRow ? `grid-row--center` : ``;
                    const oddRowStyle = isEvenRow
                        ? {
                              gap: `${getCardGap(
                                  gridEl,
                                  maxRowCount,
                                  maxGridWidths,
                                  cardWidth
                              )}px`,
                          }
                        : undefined;
                    const key = gItem?.reduce(
                        (acc: string, cur) =>
                            acc.concat(
                                `${cur.title}${cur.field_leadership_position_title}`
                            ),
                        ``
                    );
                    return (
                        <div
                            key={key}
                            className={`grid-row grid-row--cards grid-row--max-${maxRowCount} ${rowClass}`}
                            id={idx === 0 ? `grid-row-0` : ``}
                            style={oddRowStyle}
                            ref={idx === 0 ? gridWidthRef : undefined}
                        >
                            {!!gItem?.length &&
                                gItem?.map((item) => {
                                    const {
                                        image,
                                        name,
                                        pageUrl,
                                        position,
                                        secondary,
                                        topLeader,
                                        isLeadershipPage,
                                    } = getLeaderInputs(item);
                                    return (
                                        <LeaderImageCard
                                            key={name}
                                            inputs={{
                                                image,
                                                name,
                                                pageUrl,
                                                position,
                                                secondary,
                                                topLeader,
                                                isLeadershipPage,
                                            }}
                                        />
                                    );
                                })}
                        </div>
                    );
                }
            })}
        </div>
    );
};

export default LeaderGrid;
